"use client";

import React, { createContext, useEffect } from "react";
import { usePathname } from "next/navigation";
import { paywallStorageKey } from "@/components/modal/paywall-modal/paywall-modal";
import { ModalType } from "@/providers/modal-provider/modal-context.types";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";

type LocalStorageKey = "paywallModalShown";
type LocalStorageValue = "true" | "false";
type ModalPair = { localStorageKey: LocalStorageKey; modalType: ModalType };

const MODALS_TO_HANDLE: ModalPair[] = [
  { localStorageKey: paywallStorageKey, modalType: { type: "paywall-modal" } },
];

const ForcedModalContext = createContext(null);

export const useForcedModalStorage = () => {
  const { showModal } = useGlobalModalContext();

  const handleLocalStorageValue = (
    localStorageKey: LocalStorageKey,
    modalType: ModalType,
  ) => {
    const isModalShown = localStorage.getItem(localStorageKey);

    if (isModalShown === null || isModalShown === "true") {
      return false;
    }

    showModal(modalType);
    setForcedModalValue(localStorageKey, "true");

    return true;
  };

  const setForcedModalValue = (
    key: LocalStorageKey,
    value: LocalStorageValue,
  ) => {
    localStorage.setItem(key, value);
  };

  return {
    handleLocalStorageValue,
    setForcedModalValue,
  };
};

export const ForcedModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pathname = usePathname();
  const { handleLocalStorageValue, setForcedModalValue } =
    useForcedModalStorage();

  useEffect(() => {
    for (const modalPair of MODALS_TO_HANDLE) {
      const isModalOpened = handleLocalStorageValue(
        modalPair.localStorageKey,
        modalPair.modalType,
      );

      if (isModalOpened) {
        break;
      }
    }
  }, [pathname]);

  return (
    <ForcedModalContext.Provider value={null}>
      {children}
    </ForcedModalContext.Provider>
  );
};
